import { useState, useRef, useEffect } from "react";
import { ref, set, get, onValue } from "firebase/database";
import { db } from "./lib/firebase";
import { getQuestions, generateCode, parseBlocks, s, globalStyles } from "./lib/constants";
import { runGroqAnalysis } from "./lib/groq";

import ProgressBar from "./components/ProgressBar";
import HomeScreen from "./screens/HomeScreen";
import JoinScreen from "./screens/JoinScreen";
import WaitingScreen from "./screens/WaitingScreen";
import QuestionsScreen from "./screens/QuestionsScreen";
import AnalyzingScreen from "./screens/AnalyzingScreen";
import ResultScreen from "./screens/ResultScreen";
import SetupScreen from "./screens/SetupScreen";

export default function App() {
  const [screen, setScreen] = useState("home");
  const [sessionCode, setSessionCode] = useState("");
  const [joinCode, setJoinCode] = useState("");
  const [personId, setPersonId] = useState(null);
  const [myName, setMyName] = useState("");
  const [otherName, setOtherName] = useState("");
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [inputVal, setInputVal] = useState("");
  const [groqKey, setGroqKey] = useState("");
  const [rawResult, setRawResult] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState("");
  const [setupError, setSetupError] = useState("");
  const [person2Joined, setPerson2Joined] = useState(false);
  const [p1Answers, setP1Answers] = useState(null);
  const [p2Answers, setP2Answers] = useState(null);
  const [p1Name, setP1Name] = useState("");
  const [p2Name, setP2Name] = useState("");
  const [keyLoading, setKeyLoading] = useState(true);
  const listenerRef = useRef(null);

  useEffect(() => {
    async function fetchKey() {
      try {
        const snap = await get(ref(db, "config/groqKey"));
        if (snap.exists() && snap.val()) setGroqKey(snap.val());
      } catch (e) {}
      setKeyLoading(false);
    }
    fetchKey();
  }, []);

  const questions = getQuestions(otherName || "them");

  const progress =
    screen === "home" || screen === "join" || screen === "setup" ? 0 :
    screen === "waiting" ? 5 :
    screen === "questions" ? Math.round((currentQ / questions.length) * 70) + 10 :
    screen === "analyzing" ? 88 :
    screen === "result" ? 100 : 0;

  async function handleNew(name) {
    setMyName(name);
    if (!groqKey) { setScreen("setup"); return; }
    await createSession(name);
  }

  async function handleHaveCode(name) {
    setMyName(name);
    if (!groqKey) { setScreen("setup"); return; }
    setScreen("join");
  }

  async function handleKeySubmit(key) {
    setSetupError("");
    try {
      const resp = await fetch("https://api.groq.com/openai/v1/models", {
        headers: { "Authorization": `Bearer ${key}` }
      });
      if (!resp.ok) { setSetupError("This key doesn't seem to work. Double-check it and try again."); return; }
    } catch (e) {
      setSetupError("Couldn't reach Groq. Check your internet connection.");
      return;
    }
    await set(ref(db, "config/groqKey"), key);
    setGroqKey(key);
    setScreen("home");
  }

  async function createSession(name) {
    const code = generateCode();
    setSessionCode(code);
    await set(ref(db, `sessions/${code}`), {
      created: Date.now(),
      person1Done: false,
      person2Done: false,
      person2Joined: false,
      person1Name: name,
    });
    setPersonId(1);
    // Listen for person 2 joining and get their name
    const unsub = onValue(ref(db, `sessions/${code}`), (snap) => {
      const data = snap.val();
      if (data?.person2Joined) setPerson2Joined(true);
      if (data?.person2Name) setOtherName(data.person2Name);
    });
    listenerRef.current = unsub;
    setScreen("waiting");
  }

  async function joinSession() {
    if (!joinCode.trim() || joinCode.length !== 4) return;
    const snap = await get(ref(db, `sessions/${joinCode}`));
    if (!snap.exists()) { setError("Session not found. Check the code and try again."); return; }
    const data = snap.val();
    if (data.person2Joined) { setError("This session is already full."); return; }
    await set(ref(db, `sessions/${joinCode}/person2Joined`), true);
    await set(ref(db, `sessions/${joinCode}/person2Name`), myName);
    setOtherName(data.person1Name || "them");
    setSessionCode(joinCode);
    setPersonId(2);
    setScreen("questions");
  }

  async function submitAnswer() {
    if (!inputVal.trim()) return;
    const newAnswers = [...answers, inputVal.trim()];
    setAnswers(newAnswers);
    setInputVal("");
    if (currentQ + 1 < questions.length) {
      setCurrentQ(currentQ + 1);
    } else {
      const answersKey = personId === 1 ? "person1Answers" : "person2Answers";
      const doneKey = personId === 1 ? "person1Done" : "person2Done";
      await set(ref(db, `sessions/${sessionCode}/${answersKey}`), newAnswers);
      await set(ref(db, `sessions/${sessionCode}/${doneKey}`), true);
      setScreen("analyzing");
      waitForBothAndAnalyze();
    }
  }

  function waitForBothAndAnalyze() {
    const unsub = onValue(ref(db, `sessions/${sessionCode}`), async (snap) => {
      const data = snap.val();
      if (data.person1Done && data.person2Done) {
        unsub();
        const p1 = data.person1Answers;
        const p2 = data.person2Answers;
        const name1 = data.person1Name || "Person 1";
        const name2 = data.person2Name || "Person 2";
        setP1Answers(p1);
        setP2Answers(p2);
        setP1Name(name1);
        setP2Name(name2);
        setRawResult("");
        setStreaming(true);
        try {
          await runGroqAnalysis({
            groqKey,
            p1Answers: p1,
            p2Answers: p2,
            p1Name: name1,
            p2Name: name2,
            questions: getQuestions(name2),
            onToken: (text) => setRawResult(text)
          });
          setStreaming(false);
          setScreen("result");
        } catch (err) {
          await set(ref(db, "config/groqKey"), null);
          setGroqKey("");
          setError("The AI key stopped working. Someone will need to add a new one.");
          setScreen("error");
          setStreaming(false);
        }
      }
    });
  }

  function restart() {
    if (listenerRef.current) listenerRef.current();
    setScreen("home");
    setSessionCode(""); setJoinCode(""); setPersonId(null);
    setMyName(""); setOtherName("");
    setCurrentQ(0); setAnswers([]); setInputVal("");
    setRawResult(""); setError("");
    setStreaming(false); setPerson2Joined(false);
    setP1Answers(null); setP2Answers(null);
    setP1Name(""); setP2Name("");
  }

  const blocks = rawResult ? parseBlocks(rawResult) : [];

  if (keyLoading) {
    return (
      <div style={{ ...s.bg, alignItems: "center" }}>
        <div style={s.dots}>
          <span style={{ ...s.dot, animationDelay: "0s" }} />
          <span style={{ ...s.dot, animationDelay: "0.2s" }} />
          <span style={{ ...s.dot, animationDelay: "0.4s" }} />
        </div>
        <style>{globalStyles}</style>
      </div>
    );
  }

  return (
    <div style={s.bg}>
      <div style={s.container}>
        <ProgressBar progress={progress} />

        {screen === "home" && <HomeScreen onNew={handleNew} onHaveCode={handleHaveCode} />}
        {screen === "setup" && <SetupScreen onKeySubmit={handleKeySubmit} error={setupError} />}
        {screen === "join" && (
          <JoinScreen joinCode={joinCode} setJoinCode={setJoinCode} error={error}
            onJoin={joinSession} onBack={() => { setScreen("home"); setError(""); }} />
        )}
        {screen === "waiting" && (
          <WaitingScreen sessionCode={sessionCode} person2Joined={person2Joined}
            otherName={otherName} onStart={() => setScreen("questions")} />
        )}
        {screen === "questions" && (
          <QuestionsScreen currentQ={currentQ} questions={questions} personId={personId}
            inputVal={inputVal} setInputVal={setInputVal} onSubmit={submitAnswer}
            otherName={otherName} />
        )}
        {screen === "analyzing" && <AnalyzingScreen bothReady={!!(p1Answers && p2Answers)} />}
        {screen === "result" && (
          <ResultScreen streaming={streaming} blocks={blocks}
            p1Answers={p1Answers} p2Answers={p2Answers}
            p1Name={p1Name} p2Name={p2Name}
            questions={getQuestions(p2Name)} onRestart={restart} />
        )}
        {screen === "error" && (
          <div style={s.step}>
            <p style={s.display}>Something went wrong</p>
            <div style={s.errorBox}>{error}</div>
            <div style={s.btnRow}>
              <button style={s.btnPrimary} onClick={() => { setScreen("setup"); setError(""); }}>Add a new key</button>
              <button style={s.btnGhost} onClick={restart}>Back to start</button>
            </div>
          </div>
        )}

        {screen !== "setup" && (
          <button style={styles.settingsBtn} onClick={() => setScreen("setup")} title="Update API key">⚙</button>
        )}
      </div>
      <style>{globalStyles}</style>
    </div>
  );
}

const styles = {
  settingsBtn: {
    position: "fixed", bottom: "1.25rem", right: "1.25rem",
    background: "#131a14", border: "1px solid #2a3a2b",
    color: "#4a5a4b", width: 36, height: 36, borderRadius: "50%",
    cursor: "pointer", fontSize: "1rem", display: "flex",
    alignItems: "center", justifyContent: "center", lineHeight: 1,
  },
};